import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Plus, X, Trash, Phone, Link as LinkIcon } from "@phosphor-icons/react";

const KIND_LABEL = {
  manufacturer: "Manufacturer",
  extended: "Extended",
  home_warranty: "Home Warranty",
  service_contract: "Service Contract",
};

const STATUS_BADGE = {
  active: "bg-emerald-500 text-white",
  expiring_soon: "bg-amber-500 text-white",
  expired: "bg-red-600 text-white",
  unknown: "bg-slate-300 text-slate-800",
};

export default function WarrantyTracker() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [assets, setAssets] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const url = statusFilter ? `/warranties?status=${statusFilter}` : "/warranties";
      const r = await api.get(url);
      setRows(r.data || []);
    } catch { toast.error("Failed to load warranties"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const openNew = async () => {
    try {
      const r = await api.get("/assets");
      setAssets(r.data || []);
    } catch { setAssets([]); }
    setShowNew(true);
  };

  const del = async (wid) => {
    if (!window.confirm("Delete this warranty?")) return;
    try { await api.delete(`/warranties/${wid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const counts = {
    expired: rows.filter(r => r.status === "expired").length,
    expiring_soon: rows.filter(r => r.status === "expiring_soon").length,
    active: rows.filter(r => r.status === "active").length,
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="warranty-tracker-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
              <ShieldCheck weight="fill" className="text-fuchsia-500"/> Warranty Tracker
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Register manufacturer warranties, home warranties and service contracts for each appliance/asset. Never pay to fix something already covered.
            </p>
          </div>
          <button onClick={openNew} data-testid="new-warranty-btn"
            className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
            <Plus size={14}/> Register Warranty
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: "expired", label: "Expired", tone: "red", value: counts.expired },
            { key: "expiring_soon", label: "Expiring ≤ 60 Days", tone: "amber", value: counts.expiring_soon },
            { key: "active", label: "Active", tone: "emerald", value: counts.active },
          ].map(s => (
            <button key={s.key} onClick={() => setStatusFilter(statusFilter === s.key ? "" : s.key)}
              data-testid={`filter-${s.key}`}
              className={`p-4 shadow-[3px_3px_0_#0f172a] text-left transition ${
                statusFilter === s.key ? "ring-4 ring-slate-900 ring-offset-2" : ""
              } ${s.tone === "red" ? "bg-red-600 text-white" :
                    s.tone === "amber" ? "bg-amber-500 text-white" : "bg-emerald-500 text-white"}`}>
              <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{s.label}</div>
              <div className="text-4xl font-black mt-1">{s.value}</div>
            </button>
          ))}
        </div>

        <div className="rugged-card p-4">
          <h3 className="font-black uppercase tracking-widest text-sm mb-3">
            {statusFilter ? `Filtered · ${statusFilter.replace("_", " ")}` : "All Warranties"}
          </h3>
          {busy ? <div className="text-sm text-slate-500 py-4">Loading…</div>
          : rows.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No warranties yet. Register one to start tracking.</div>
          ) : (
            <div className="space-y-2">
              {rows.map(w => (
                <div key={w.id} className="p-3 border-2 border-slate-200 space-y-1" data-testid={`warranty-row-${w.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-sm">{w.asset_name || "Asset"} · {KIND_LABEL[w.kind] || w.kind}</div>
                      <div className="text-[10px] text-slate-500">
                        {w.provider} · Policy {w.policy_number || "—"} · Started {w.starts || "—"}
                      </div>
                    </div>
                    <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${STATUS_BADGE[w.status]}`}>
                      {w.status.replace("_", " ")}
                    </span>
                  </div>
                  {w.coverage_summary && (
                    <div className="text-[11px] text-slate-700 line-clamp-2">{w.coverage_summary}</div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-100">
                    <div className="text-[10px] text-slate-600">
                      Expires <b>{w.expires || "—"}</b>
                      {w.days_until !== null && w.days_until !== undefined && (
                        <span className={w.days_until < 0 ? " text-red-600 font-bold ml-1" : " ml-1"}>
                          ({w.days_until < 0 ? `${-w.days_until}d ago` : `in ${w.days_until}d`})
                        </span>
                      )}
                    </div>
                    {w.claim_phone && (
                      <a href={`tel:${w.claim_phone}`} data-testid={`claim-phone-${w.id}`}
                        className="ml-auto h-7 px-2 border-2 border-slate-900 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                        <Phone size={10}/> {w.claim_phone}
                      </a>
                    )}
                    {w.claim_url && (
                      <a href={w.claim_url} target="_blank" rel="noopener noreferrer" data-testid={`claim-url-${w.id}`}
                        className="h-7 px-2 border-2 border-slate-900 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                        <LinkIcon size={10}/> Claim
                      </a>
                    )}
                    <button onClick={() => del(w.id)} data-testid={`warranty-delete-${w.id}`}
                      className="h-7 w-7 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                      <Trash size={12}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showNew && (
          <WarrantyModal assets={assets} onClose={() => setShowNew(false)}
            onSaved={() => { setShowNew(false); load(); }}/>
        )}
      </div>
    </AdminShell>
  );
}

function WarrantyModal({ assets, onClose, onSaved }) {
  const [form, setForm] = useState({
    asset_id: "", provider: "", kind: "manufacturer", policy_number: "",
    starts: "", expires: "", coverage_summary: "", claim_phone: "", claim_url: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.asset_id || !form.provider) return toast.error("Asset + Provider required");
    setBusy(true);
    try {
      await api.post("/warranties", form);
      toast.success("Warranty registered");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="warranty-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] max-h-[90vh] overflow-y-auto">
        <div className="p-3 bg-fuchsia-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">Register Warranty</div>
          <button onClick={onClose} data-testid="warranty-modal-close" className="p-1 hover:bg-fuchsia-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <select value={form.asset_id} onChange={e => setForm({...form, asset_id: e.target.value})} data-testid="warranty-asset-select"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
            <option value="">— Select asset —</option>
            {assets.map(a => (
              <option key={a.id || a._id} value={a.id || a._id}>
                {(a.name || a.type || "Asset")}{a.make ? ` · ${a.make}` : ""}{a.address ? ` · ${a.address}` : ""}
              </option>
            ))}
          </select>
          <input value={form.provider} onChange={e => setForm({...form, provider: e.target.value})} placeholder="Provider (e.g., LG, Whirlpool, AWR)" data-testid="warranty-provider"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <select value={form.kind} onChange={e => setForm({...form, kind: e.target.value})}
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input value={form.policy_number} onChange={e => setForm({...form, policy_number: e.target.value})} placeholder="Policy / Registration #"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.starts} onChange={e => setForm({...form, starts: e.target.value})} placeholder="Starts (YYYY-MM-DD)"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={form.expires} onChange={e => setForm({...form, expires: e.target.value})} placeholder="Expires (YYYY-MM-DD)" data-testid="warranty-expires"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <textarea value={form.coverage_summary} onChange={e => setForm({...form, coverage_summary: e.target.value})} rows={2} placeholder="Coverage summary"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.claim_phone} onChange={e => setForm({...form, claim_phone: e.target.value})} placeholder="Claim phone"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={form.claim_url} onChange={e => setForm({...form, claim_url: e.target.value})} placeholder="Claim URL"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <button onClick={save} disabled={busy || !form.asset_id || !form.provider} data-testid="warranty-save-btn"
            className="w-full h-11 bg-fuchsia-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Register Warranty"}
          </button>
        </div>
      </div>
    </div>
  );
}
